/**
 * Deterministic export / handover generators — pure templates over the
 * structured incident, live insights and audit log. No AI: this is the
 * always-available baseline (works with `AI_ENABLED=false`); when AI is on the
 * LLM may only *rephrase* this output under the grounding contract.
 *
 * Copy stays advisory and factual — a handover records the picture and what is
 * outstanding; it never issues commands to the oncoming officer.
 */

import type { Incident } from './incident.js';
import { labelForTag, STRATEGY_LABELS, CONFIRMATION_LABELS } from './incident.js';
import { STAGE_LABELS } from './stages.js';
import { buildRadioSitrep } from './sitrep.js';
import { summariseCapability, RESOURCE_STATUS_LABELS, APPLIANCE_REFERENCE } from './resources.js';
import { DEFAULT_CONFIG } from './config.js';
import type { Insight, InsightSeverity } from './insights/index.js';
import type { AuditEvent } from './audit.js';

const NL = '\n';

const fmtTime = (iso: string): string => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toISOString().replace('T', ' ').slice(0, 16) + 'Z';
};

/** Severity order for ranking outstanding items in a handover. */
const SEV_RANK: Record<InsightSeverity, number> = { critical: 0, high: 1, medium: 2, info: 3 };

/** Insights still needing attention (not dismissed / auto-resolved), ranked. */
function outstandingInsights(insights: readonly Insight[]): Insight[] {
  return insights
    .filter((i) => i.status !== 'dismissed' && i.status !== 'auto-resolved')
    .slice()
    .sort((a, b) => SEV_RANK[a.severity] - SEV_RANK[b.severity]);
}

/**
 * A full written handover, suitable for a relief/oncoming officer or the file.
 * Plain text with simple headed sections — copy/paste and print friendly.
 */
export function buildWrittenHandover(
  incident: Incident,
  insights: readonly Insight[] = [],
  generatedAt: string = new Date().toISOString(),
): string {
  const cap = summariseCapability(incident.resources, DEFAULT_CONFIG.water.bulkWaterMinL);
  const out: string[] = [];

  out.push(`HANDOVER — ${incident.name}`);
  out.push(`${incident.location}${incident.locality ? ` (${incident.locality})` : ''}`);
  out.push(
    `Stage: ${STAGE_LABELS[incident.stage]}  •  Status: ${CONFIRMATION_LABELS[incident.status]}  •  ${incident.level}`,
  );
  out.push(
    `Lead: ${incident.leadAgency}${incident.supportingAgencies.length ? `  •  Supporting: ${incident.supportingAgencies.join(', ')}` : ''}`,
  );
  out.push(`Generated: ${fmtTime(generatedAt)} (advisory — supports command judgement, does not replace it)`);
  out.push('');

  // Current picture (ITASC) — reuse the deterministic radio SITREP body.
  out.push('CURRENT PICTURE (ITASC)');
  out.push(buildRadioSitrep(incident));
  out.push('');

  // Resources on scene.
  out.push('RESOURCES');
  if (incident.resources.length === 0) {
    out.push('No units recorded.');
  } else {
    for (const u of incident.resources) {
      const spec = APPLIANCE_REFERENCE[u.type];
      out.push(
        `- ${u.callsign} (${spec.label}, ${u.agency}) — ${RESOURCE_STATUS_LABELS[u.status]}; ` +
          `crew ${u.crewCount} (BA ${u.baOperators}), water ${u.onboardWaterL} L` +
          (typeof u.etaMin === 'number' ? `, ETA ${u.etaMin} min` : ''),
      );
    }
    out.push(
      `Committed: ${cap.unitCount} unit(s); crew ${cap.crewCount} (BA operators ${cap.baOperators}); ` +
        `pumpers ${cap.pumpers}; onboard water ${cap.onboardWaterL} L; ` +
        `bulk water ${cap.hasBulkWater ? 'assigned' : 'not assigned'}.`,
    );
  }
  out.push('');

  // Water plan.
  const wp = incident.waterPlan;
  out.push('WATER');
  const waterBits: string[] = [];
  if (typeof wp.nearestHydrantDistanceM === 'number')
    waterBits.push(`nearest hydrant ~${Math.round(wp.nearestHydrantDistanceM)} m`);
  else waterBits.push('nearest hydrant unknown');
  if (wp.staticWaterAvailable) waterBits.push(`static water available${wp.staticWaterNote ? ` (${wp.staticWaterNote})` : ''}`);
  if (wp.relayOrShuttle) waterBits.push('relay/shuttle in place');
  out.push(waterBits.join('; ') + '.');
  out.push('');

  // Outstanding — what the oncoming officer should be aware of.
  const outstanding = outstandingInsights(insights);
  out.push('OUTSTANDING / FOR AWARENESS');
  if (outstanding.length === 0) {
    out.push('No open advisories.');
  } else {
    for (const i of outstanding) {
      out.push(`- [${i.severity.toUpperCase()}] ${i.title}${i.status === 'acknowledged' ? ' (acknowledged)' : ''}`);
      if (i.message) out.push(`    ${i.message}`);
    }
  }
  out.push('');
  out.push('Prepared as decision support. Confirm all facts before acting.');

  return out.join(NL);
}

/** A concise spoken handover for a relief officer — short, factual, advisory. */
export function buildRadioHandover(incident: Incident, insights: readonly Insight[] = []): string {
  const cap = summariseCapability(incident.resources, DEFAULT_CONFIG.water.bulkWaterMinL);
  const it = incident.itasc;
  const parts: string[] = [];

  parts.push(`${incident.name} at ${incident.location}`);
  parts.push(`${STAGE_LABELS[incident.stage].toLowerCase()}, ${CONFIRMATION_LABELS[incident.status].toLowerCase()}`);
  if (it.actions.strategy) parts.push(`strategy ${STRATEGY_LABELS[it.actions.strategy].toLowerCase()}`);
  parts.push(`${cap.unitCount} unit(s) committed, ${cap.crewCount} crew (${cap.baOperators} BA operators)`);
  if (it.support.unresolvedGaps.length)
    parts.push(`outstanding ${it.support.unresolvedGaps.map(labelForTag).join(', ').toLowerCase()}`);

  const critical = outstandingInsights(insights).filter((i) => i.severity === 'critical' || i.severity === 'high');
  if (critical.length) parts.push(`for awareness: ${critical.map((i) => i.title).join('; ')}`);

  return parts.join('. ') + '.';
}

/** Audit log as CSV (one row per event) — for the post-incident record. */
export function exportAuditCsv(events: readonly AuditEvent[]): string {
  const esc = (v: unknown): string => {
    const s = v == null ? '' : typeof v === 'object' ? JSON.stringify(v) : String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const header = ['at', 'type', 'actor', 'source', 'payload'];
  const rows = events.map((e) => [e.at, e.type, e.actor, e.source, e.payload ? JSON.stringify(e.payload) : ''].map(esc).join(','));
  return [header.join(','), ...rows].join(NL);
}

export type HandoverFormat = 'written' | 'radio';

/** Filename stem for a download (no extension), safe across platforms. */
export function exportFilename(incident: Incident, kind: string): string {
  const slug = incident.name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') || 'incident';
  const stamp = new Date().toISOString().slice(0, 16).replace(/[-:]/g, '').replace('T', '-');
  return `${slug}-${kind}-${stamp}`;
}
